/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React from 'react';

import { cssMap, cx, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import Image from '@atlaskit/image';
import { Anchor, Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

export type RelatedCardProps = {
	title: string;
	description: string;
	image: {
		src: string;
		srcDark?: string;
		alt: string;
	};
	testId?: string;
	href?: string;
};

const styles = cssMap({
	root: {
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderRadius: token('radius.xlarge'),
		overflow: 'hidden',

		'@media (min-width: 30rem)': {
			display: 'flex',
			alignItems: 'center',
		},
	},
	imageContainer: {
		height: '100%',
		'@media (min-width: 30rem)': {
			flex: '0 0 50%',
		},
	},
	link: {
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'none',
			borderColor: token('color.border.bold'),
		},
	},
	image: {
		display: 'block',
	},
	contentContainer: {
		'@media (min-width: 48rem)': {
			flex: '0 0 50%',
		},
		paddingBlockStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		paddingBlockEnd: token('space.200'),
		paddingInlineStart: token('space.200'),
	},
});

/**
 * __RelatedCard__
 *
 * A RelatedCard shows related content.
 *
 */
const RelatedCard: ({
	title,
	description,
	testId,
	image,
	href,
}: RelatedCardProps) => JSX.Element = ({
	title,
	description,
	testId,
	image,
	href,
}: RelatedCardProps) => {
	const content = (
		<>
			<Box xcss={styles.imageContainer}>
				<Image
					src={image.src}
					srcDark={image.srcDark}
					alt={image.alt || ''}
					// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
					css={styles.image}
				/>
			</Box>

			<Box xcss={styles.contentContainer}>
				<Stack space="space.150">
					<Heading size="small">{title}</Heading>
					<Text as="p">{description}</Text>
				</Stack>
			</Box>
		</>
	);

	return href ? (
		<Anchor href={href} testId={testId} xcss={cx(styles.root, styles.link)}>
			{content}
		</Anchor>
	) : (
		<Box testId={testId} xcss={styles.root}>
			{content}
		</Box>
	);
};

export default RelatedCard;
